import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable';
import { useAdminQuestionCategories, QuestionCategory } from '@/api/useAdminQuestionCategories';
import Routes from '@/router/Routes';

const QuestionCategoriesPage: FC = () => {
  const navigate = useNavigate();
  const { data: categories, isLoading } = useAdminQuestionCategories();

  const columns: ColumnDef<QuestionCategory>[] = useMemo(
    () => [
      {
        accessorKey: 'subject',
        header: 'Subject',
        enableSorting: false,
        cell: (info) => {
          const subject = info.getValue() as string;
          return <span className="text-gray-900">{subject}</span>;
        },
      },
      {
        accessorKey: 'paper_number',
        header: 'Paper',
        enableSorting: false,
        cell: (info) => <span className="text-gray-600">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'category',
        header: 'Category',
        enableSorting: false,
        cell: (info) => (
          <span className="text-gray-900 font-mono font-medium">{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: (info) => <span className="text-gray-900">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: (info) => (
          <span className="text-gray-600 text-sm">{(info.getValue() as string) || '-'}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            onClick={() => navigate(Routes.ADMIN_EDIT_CATEGORY.replace(':categoryId', row.original.id))}
            className="text-[#C87941] font-semibold hover:text-[#A66835] transition-colors"
          >
            Edit
          </button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white rounded-3xl flex justify-between items-center px-8 py-6">
        <h1 className="text-3xl font-semibold text-gray-900">Question Categories</h1>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl overflow-hidden shadow-sm mt-6">
        <DataTable
          data={categories ?? []}
          columns={columns}
          wrapperClassName="bg-white"
          headClassName="bg-gray-50 border-t border-gray-100"
          rowClassName="hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          showPagination={false}
          loading={isLoading}
        />
      </div>
    </div>
  );
};

export default QuestionCategoriesPage;
